import { Link } from 'react-router-dom';
import { To } from 'history';

// material-ui
import { SxProps } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import Box from '@mui/material/Box';

// project imports
import { APP_DEFAULT_PATH } from 'config';
import logo from 'assets/images/logo/Permit-Rockstar.png';
import logo_wide from 'assets/images/logo/Permit-Rockstar-Wide.png';
import useTenant from 'hooks/useTenant';
import useAuth from 'hooks/useAuth';
import { Typography, Stack, Avatar, Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';

// ==============================|| MAIN LOGO ||============================== //

interface Props {
  reverse?: boolean;
  isIcon?: boolean; // true = small icon, false = full logo
  authVariant?: boolean; // true = for login page
  sx?: SxProps;
  to?: To;
}

export default function LogoSection({
  reverse,
  isIcon,
  authVariant,
  sx,
  to
}: Props) {
  const size = authVariant
    ? { width: 150, height: 'auto' } // auth/login page size
    : isIcon
      ? { width: 50, height: 50 } // sidebar icon - increased for better visibility
      : { width: '100%', height: '100%' }; // normal header - bigger for better clarity

  const { branding, isLoading } = useTenant();
  const { user } = useAuth();
  const logoImage = authVariant ? logo : isIcon ? logo : logo;
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [branding?.tenantLogoUrl]);

  // Render content based on branding availability
  const renderLogo = () => {
    // 0. For Super Admin login or Super Admin user, always show default App Logo
    if (authVariant || user?.userType === 1) {
      return (
        <img
          src={logoImage}
          alt="private-provider-portal"
          style={{
            width: size.width,
            height: size.height,
            objectFit: 'contain',
            display: 'block',
            filter: isIcon ? 'brightness(0.9)' : 'none',
            marginTop: !authVariant && !isIcon ? '10px' : '0',
          }}
        />
      );
    }

    // 0b. Loading state for tenants/contractors to prevent flash of default logo
    if (isLoading && !branding && (user?.userType === 2 || user?.userType === 4)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: size.height }}>
          <Skeleton
            variant="rounded"
            width={isIcon ? 40 : 180}
            height={isIcon ? 40 : 40}
            animation="wave"
          />
        </Box>
      );
    }

    // 1. Tenant Logo (Image)
    if (branding?.tenantLogoUrl && !imageError) {
      return (
        <img
          src={branding.tenantLogoUrl}
          alt={branding.companyName || "Tenant Logo"}
          style={{
            width: isIcon ? 45 : 'auto',
            height: isIcon ? 45 : '100%',
            maxWidth: 150,
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto',
          }}
          onError={() => setImageError(true)}
        />
      );
    }

    // 2. Tenant Name (Text) - If no logo url
    if (branding?.companyName) {
      if (isIcon) {
        // Initials for collapsed sidebar
        const initials = branding.companyName
          .split(' ')
          .map(n => n[0])
          .slice(0, 2)
          .join('')
          .toUpperCase();

        return (
          <Avatar
            variant="rounded"
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 600
            }}
          >
            {initials}
          </Avatar>
        );
      }

      // Full Name for expanded/header
      return (
        <Typography
          variant="h4"
          component="div"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            textAlign: 'center',
            lineHeight: 1.2,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            px: 1
          }}
        >
          {branding.companyName}
        </Typography>
      );
    }

    // 3. Default App Logo
    return (
      <img
        src={logoImage}
        alt="private-provider-portal"
        style={{
          width: size.width,
          height: size.height,
          objectFit: 'contain',
          display: 'block',
          filter: isIcon ? 'brightness(0.9)' : 'none',
          marginTop: !authVariant && !isIcon ? '10px' : '0',
        }}
      />
    );
  };

  let linkTo = to || APP_DEFAULT_PATH;
  if (!to) {
    if (user?.userType === 2) {
      linkTo = '/tenant-dashboard';
    } else if (user?.userType === 4) {
      linkTo = '/contractor-dashboard';
    }
  }

  return (
    <ButtonBase
      disableRipple
      component={Link}
      to={linkTo}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        borderRadius: 1,
        transition: 'all 0.2s ease-in-out',
        ...sx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%'
        }}
      >
        {renderLogo()}
      </Box>
    </ButtonBase>
  );
}
